import { ReactNode, useMemo, useState } from 'react';

import { api } from '@/api';
import { useUser } from '@/core/user';
import { createJiraSubmitter } from '@/shared/util';

import { ReportIssueHandlerContext } from './report-issue-handler.context';
import {
  AboutType,
  IssueType,
  IssueTypeValues,
  ReportIssueHandlerState,
} from './report-issue-handler.model';
import { getShortAboutType } from './report-issue-handler.util';

export interface ReportIssueHandlerProviderProps {
  children: ReactNode;
}

export function ReportIssueHandlerProvider({ children }: ReportIssueHandlerProviderProps) {
  const [issueType, setIssueType] = useState<IssueTypeValues>();
  const [aboutType, setAboutType] = useState<AboutType>();
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showErrors, setShowErrors] = useState(false);

  const { user } = useUser();

  const {
    isError: isReportIssueError,
    isPending: isReportIssuePending,
    isSuccess: isReportIssueSuccess,
    mutate: reportIssue,
    reset: resetReportIssue,
  } = api.fct.reportIssue.mutation.usePostReportIssue();

  const {
    isError: isReportServiceDeskIssueError,
    isPending: isReportServiceDeskIssuePending,
    isSuccess: isReportServiceDeskIssueSuccess,
    mutate: reportServiceDeskIssue,
    reset: resetReportServiceDeskIssue,
  } = api.fct.reportServiceDeskIssue.mutation.usePostReportServiceDeskIssue();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter((file) => {
      const isValidType = ['image/jpeg', 'image/png', 'image/gif'].includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024;

      return isValidType && isValidSize;
    });

    setAttachedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setIssueType(undefined);
    setAboutType(undefined);
    setTitle('');
    setDescription('');
    setAttachedFiles([]);
  };

  const handleSubmit = async () => {
    if (!title || !issueType) {
      setShowErrors(true);

      return;
    }

    const jiraSubmitter = createJiraSubmitter({
      email: user.email,
      name: user.fullname,
      prenuvoID: user.prenuvoID,
    });

    const postData = jiraSubmitter({
      description,
      issueTypeName: issueType === IssueType.REPORT_ISSUE ? 'Bug' : 'Story',
      projectKey: 'FC',
      title: `${aboutType ? getShortAboutType(aboutType) : ''} ${title}`,
    });

    if (issueType !== IssueType.REPORT_ISSUE) {
      await reportIssue({ payload: postData });
      resetForm();
    } else {
      await reportServiceDeskIssue({
        payload: {
          description: postData.description,
          email: user.email,
          issuetype: postData.issuetype,
          project: postData.project,
          summary: postData.summary,
        },
      });
      resetForm();
    }
  };

  const value: ReportIssueHandlerState = useMemo(
    () => ({
      aboutType,
      attachedFiles,
      description,
      handleFileChange,
      handleSubmit,
      isReportIssueError,
      isReportIssuePending,
      isReportIssueSuccess,
      isReportServiceDeskIssueError,
      isReportServiceDeskIssuePending,
      isReportServiceDeskIssueSuccess,
      issueType,
      removeFile,
      resetForm,
      resetReportIssue,
      resetReportServiceDeskIssue,
      setAboutType,
      setDescription,
      setIssueType,
      setShowErrors,
      setTitle,
      showErrors,
      title,
    }),
    [
      aboutType,
      attachedFiles,
      description,
      handleFileChange,
      handleSubmit,
      isReportIssueError,
      isReportIssuePending,
      isReportIssueSuccess,
      isReportServiceDeskIssueError,
      isReportServiceDeskIssuePending,
      isReportServiceDeskIssueSuccess,
      issueType,
      removeFile,
      resetForm,
      resetReportIssue,
      resetReportServiceDeskIssue,
      setAboutType,
      setDescription,
      setIssueType,
      setShowErrors,
      setTitle,
      showErrors,
      title,
    ],
  );

  return (
    <ReportIssueHandlerContext.Provider value={value}>
      {children}
    </ReportIssueHandlerContext.Provider>
  );
}
